import { isTopic } from '@tunein/web-common';
import classNames from 'clsx';
import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { displayAds } from 'src/client/displayAds';
import { buildContentTargeting } from 'src/client/displayAds/buildContentTargeting';
import { player } from 'src/client/player';
import { REDIRECT_CATEGORY_ID } from 'src/common/constants/ads/location';
import {
  BOTTOM_BANNER_ENABLED,
  FREESTAR_ENABLED,
  FREESTAR_PRIMIS_VIDEO_ENABLED,
} from 'src/common/constants/experiments/ads';
import useOnMount from 'src/common/hooks/useOnMount';
import { useStores } from 'src/common/providers';
import { selectExperiment } from 'src/common/selectors/config';
import { mintSingleton } from '../../../client/mint';
import { logClientError, logWebActivity } from '../../actions/logging';
import { doNotFetch } from '../../constants/locationState';
import { TOPIC_ID } from '../../constants/queryParams';
import connectWithFailureState from '../../decorators/connectWithFailureState';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import getRadioContext from '../../routes/utils/getRadioContext';
import { selectIsAuthenticated } from '../../selectors/auth';
import commonCss from '../../styles/common.module.scss';
import { fetchProfile } from '../../utils/connectors/fetchProfile';
import ifThenAppendCBSTritonScript from '../../utils/dom/ifThenAppendCBSTritonScript';
import fullUrl from '../../utils/fullUrl';
import { buildGuideItemPathnameWithLogger } from '../../utils/guideItem/getGuideItemPathname';
import isCBSAffiliated from '../../utils/guideItem/isCBSAffiliated';
import {
  behaviors as GuideItemBehaviors,
  isCategory,
  isExpiredGame,
  isGame,
  isPodcastProfile,
  isShow,
  types,
} from '../../utils/guideItemTypes';
import ContainerItemsLoader from '../containerItems/ContainerItemsLoader';
import SeoHelmet from '../SeoHelmet';
import canonicalLinkObj from '../utils/canonicalLinkObj';
import { createAndroidDeepLinks } from '../utils/createAndroidDeepLinks';
import { createInternationalSeoLinks } from '../utils/createInternationalSeoLinks';
import { createProfileMetaDescription } from '../utils/pageMetaDescriptions';
import { createProfilePageMetaKeywords } from '../utils/pageMetaKeywords';
import { createProfilePageTitle } from '../utils/pageTitles';
import css from './profile.module.scss';
import createOEmbedLinks from './utils/createOEmbedLinks';
import createOgMeta from './utils/createOgMeta';
import handleAutoplayCheck from './utils/handleAutoplayCheck';

export function Profile({
  isAuthenticated,
  guideItem,
  routeProps,
  selectedGuideId,
  actions,
  topicGuideId,
  isMobile,
  allowAutoplay,
  breakpoint,
  isTopicIdInvalid,
  history,
  match,
  location,
  isDiscord,
  topicGuideItem,
  isRightSideOpen,
}) {
  const locationAndLocalizationContext = useContext(LocationAndLocalizationContext);
  const [canBrowserAutoplay, setCanBrowserAutoplay] = useState(false);
  const { playerStore, appStore } = useStores();
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const guideItemPathname = appStore.guideItemPathname();

  const autoPlay = useCallback(async () => {
    if (!allowAutoplay) {
      return;
    }

    await player.ready;

    // topicGuideItem and guideItem are root guide items, we need to fetch the childGuideItem
    // We should have profile child guide item for a profile availbale from root guide item
    const childGuideItem = (topicGuideItem || guideItem)?.containers?.[0]?.children?.[0];

    if (player.canAutoplay && player.isIdle()) {
      player.onClick(childGuideItem, {
        isUserInitiated: false,
        reportingData: { playSource: 'PLAY_SOURCE_AUTOPLAY' },
      });
    }
  }, [allowAutoplay, topicGuideItem, guideItem]);

  useEffect(() => {
    const { location, station } = guideItem.metadata?.properties || {};

    mintSingleton.updateState('countryRegionId', location?.countryRegionId);
    mintSingleton.updateState('stationLanguage', station?.language);
    mintSingleton.updateState('selectedGuideId', selectedGuideId);
    displayAds.setPageTargeting({
      country_region_id: location?.countryRegionId,
      station_language: station?.language,
    });
    // withAdScreenRequest also derives this, but it runs on route change — before the profile
    // fetch resolves — so on a cold load it would see no guide item. Re-push once the data is in
    // hand, which is the same moment the legacy path's `selectedGuideId` update makes it visible.
    displayAds.setContentTargeting(buildContentTargeting(guideItem));
  }, [selectedGuideId, guideItem.metadata?.properties]);

  useOnMount(() => {
    if (isCBSAffiliated(guideItem)) {
      ifThenAppendCBSTritonScript();
    }

    const behaviorAction = guideItem.behaviors?.primaryButton?.actionName;
    const subscriptionRequired = behaviorAction === GuideItemBehaviors.subscribe;
    const isPlayerIdle = !player.isReady || player.isIdle();
    const canAutoPlay = !subscriptionRequired && isPlayerIdle && !isDiscord;

    // In the case that a topicid is supplied, but it isn't valid (e.g. fails to load), we
    // shouldn't attempt to autoplay it.
    if ((!topicGuideId && canAutoPlay) || (!isTopicIdInvalid && canAutoPlay)) {
      handleAutoplayCheck(() => {
        autoPlay();
        setCanBrowserAutoplay(true);
      }).catch(() => actions.logWebActivity('autoPlayDisabled', true, guideItem.guideId));
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: run when tundedGuideId changes
  useEffect(() => {
    const { search, pathname } = location;

    // Check that now playing guideItem matches current profile guideItem
    if (
      isTopic(tunedGuideId) &&
      guideItemPathname?.split('?')?.shift() === pathname &&
      !isDiscord
    ) {
      const searchParams = new URLSearchParams(search);
      searchParams.set(TOPIC_ID, tunedGuideId.slice(1));
      history?.replace({
        pathname,
        search: searchParams.toString(),
        state: {
          [doNotFetch]: true,
        },
      });
    }
  }, [tunedGuideId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Logic moved from componentDidUpdate
  useEffect(() => {
    if (!selectedGuideId) {
      return;
    }

    const redirectCategoryId = location.state?.[REDIRECT_CATEGORY_ID];
    if (redirectCategoryId && isCategory(redirectCategoryId)) {
      player.setAppSessionData({ categoryId: redirectCategoryId });
      displayAds.setPageTargeting({ categoryId: redirectCategoryId });
      mintSingleton.updateState('categoryId', redirectCategoryId);
    }
  }, [selectedGuideId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Logic moved from componentDidUpdate
  useEffect(() => {
    if (isAuthenticated !== undefined) {
      actions.fetchProfile(getRadioContext, { history, location, match });
    }
  }, [isAuthenticated]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Logic moved from componentDidUpdate
  useEffect(() => {
    if (canBrowserAutoplay && allowAutoplay) {
      autoPlay();
    }
  }, [allowAutoplay, canBrowserAutoplay]);

  useEffect(() => {
    return () => {
      // Notify Mint that we're no longer on the profile page
      mintSingleton.updateState('selectedGuideId', null);
      mintSingleton.updateState('categoryId', null);
      displayAds.setPageTargeting({
        categoryId: null,
        country_region_id: null,
        station_language: null,
      });
      displayAds.setContentTargeting({});
      player.setAppSessionData({ categoryId: null });
    };
  }, []);

  const { getLocalizedText } = locationAndLocalizationContext;
  const properties = guideItem.metadata?.properties || {};
  const {
    station: { band, callSign, frequency } = {},
    program,
    seoInfo: { keywords: guideItemSEOKeywords } = {},
    guideInfo: { description: guideInfoDescription } = {},
  } = properties || {};
  const {
    actions: { share: { logoUrl } = {}, play: { isLive } = {} } = {},
    gameInfo = {},
    a11yTitle: guideItemTitle,
    type: guideItemType,
    rootGenre,
  } = guideItem;

  const pageTitle = createProfilePageTitle({
    getLocalizedText,
    title: guideItemTitle,
    properties,
    type: guideItemType,
  });

  const description = createProfileMetaDescription({
    getLocalizedText,
    type: topicGuideItem?.type || guideItemType,
    description: topicGuideItem?.description || guideItem.description,
    title: guideItemTitle,
    ...properties,
  });

  const keywords = createProfilePageMetaKeywords({
    title: guideItemTitle,
    rootGenre,
    keywords: guideItemSEOKeywords,
  });
  const guideItemPathName = buildGuideItemPathnameWithLogger(guideItem)(actions.logClientError);
  const links = [
    canonicalLinkObj(guideItemPathName),
    ...createInternationalSeoLinks(
      guideItemPathName,
      properties?.station?.languageCode || properties?.program?.languageCode,
    ),
    ...createOEmbedLinks(guideItem.guideId, guideItemTitle, guideItem.actions?.embed),
    ...createAndroidDeepLinks(guideItem.guideId),
  ];

  const fullUrlWithAbsoluteRoot = fullUrl(guideItemPathName);

  const meta = [
    ...(isExpiredGame(program) ? [{ name: 'robots', content: 'noindex' }] : []),
    ...createOgMeta(
      guideItem,
      fullUrlWithAbsoluteRoot,
      topicGuideItem?.title || pageTitle,
      description,
    ),
  ];

  const generateStructuredData = () => {
    if (guideItemType === types.station) {
      return {
        '@type': 'RadioBroadcastService',
        '@id': 'RadioBroadcastService',
        callSign,
        broadcastDisplayName: callSign,
        broadcaster: callSign,
        broadcastFrequency: frequency
          ? {
              '@type': 'BroadcastFrequencySpecification',
              broadcastFrequencyValue: frequency || undefined,
              broadcastSignalModulation: band,
            }
          : undefined,
      };
    }

    if (guideItemType === types.program) {
      const eventStrucutredData = {
        '@type': 'BroadcastEvent',
        isLiveBroadcast: isLive,
        startDate: program?.eventStartTimeUtc,
      };

      if (isShow(program)) {
        return eventStrucutredData;
      }

      if (isGame(program)) {
        return {
          ...eventStrucutredData,
          broadcastOfEvent: {
            '@type': 'SportsEvent',
            name: guideItemTitle,
            image: gameInfo?.firstTeamLogoUrl,
            organizer: {
              '@type': 'Organization',
              name: 'TuneIn',
              url: 'https://tunein.com',
            },
            homeTeam: {
              '@type': 'SportsTeam',
              name: gameInfo?.firstTeamTitle,
              image: gameInfo?.firstTeamLogoUrl,
            },
            awayTeam: {
              '@type': 'SportsTeam',
              name: gameInfo?.secondTeamTitle,
              image: gameInfo?.secondTeamLogoUrl,
            },
            startDate: program?.eventStartTimeUtc,
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
            location: {
              '@type': 'VirtualLocation',
              url: fullUrlWithAbsoluteRoot,
            },
          },
        };
      }

      if (isPodcastProfile(guideItem)) {
        if (rootGenre === 'audiobooks') {
          const getPropertyFromDescription = (property) => {
            const re = new RegExp(`${property} - [^.\n\r]+`, 'g'); // eslint-disable-line no-useless-escape
            const str = guideInfoDescription.match(re)?.[0];

            if (str) {
              return str.replace(`${property} - `, '');
            }
          };

          const datePublished = getPropertyFromDescription('Published Date');
          const author = getPropertyFromDescription('Author');
          const narrator = getPropertyFromDescription('Narrator');

          return {
            '@type': 'Audiobook',
            '@id': fullUrlWithAbsoluteRoot,
            datePublished,
            author: author
              ? {
                  '@type': 'Person',
                  name: author,
                }
              : undefined,
            readBy: narrator
              ? {
                  '@type': 'Person',
                  name: narrator,
                }
              : undefined,
            provider: 'TuneIn',
            mainEntityOfPage: fullUrlWithAbsoluteRoot,
          };
        }

        return {
          '@type': 'PodcastSeries',
        };
      }
    }

    return {};
  };

  return (
    <div
      className={classNames(css.container, {
        [css.discord]: isDiscord,
        [css.mobile]: isMobile,
      })}
    >
      <SeoHelmet
        url={fullUrlWithAbsoluteRoot}
        title={pageTitle}
        name={guideItemTitle}
        image={logoUrl}
        description={description}
        keywords={keywords}
        meta={meta}
        links={links}
        additionalJsonLd={generateStructuredData()}
        includeJsonLd
      />
      <div className={commonCss.containerItemsWrapper}>
        <ContainerItemsLoader
          guideItem={guideItem}
          items={guideItem.containers}
          itemsStyles={guideItem.metadata?.styles}
          isFetching={guideItem.isFetching}
          selectedContentId={selectedGuideId}
          breakpoint={breakpoint}
          routeProps={routeProps}
          isRightSideOpenOnProfilePage={isRightSideOpen}
          isProfilePage
        />
      </div>
    </div>
  );
}

Profile.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  guideItem: PropTypes.object.isRequired,
  routeProps: PropTypes.object.isRequired,
  selectedGuideId: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
  topicGuideId: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  allowAutoplay: PropTypes.bool.isRequired,
  breakpoint: PropTypes.number.isRequired,
  isTopicIdInvalid: PropTypes.bool.isRequired,
  canShowAds: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  isDiscord: PropTypes.bool.isRequired,
  isFreestarEnabled: PropTypes.bool.isRequired,
  isBottomBannerEnabled: PropTypes.bool.isRequired,
  isFreestarPrimisVideoEnabled: PropTypes.bool.isRequired,
  sponsorshipEnabled: PropTypes.bool.isRequired,
  isRightSideOpen: PropTypes.bool.isRequired,
};

function getProfile(state, props) {
  return state.profiles[props?.routeProps?.guideContext?.guideId];
}

export function mapStateToProps(state, ownProps) {
  const { guideId, topicGuideId } = ownProps?.routeProps?.guideContext || {};
  const { profiles, app } = state;
  const { isMobile, allowAutoplay } = app;
  const guideItem = profiles[guideId];
  const topicGuideItem = profiles[topicGuideId];
  const hasTopicFetchFailed = profiles?.[topicGuideId]?.hasFailed;

  return {
    topicGuideItem,
    isAuthenticated: selectIsAuthenticated(state),
    selectedGuideId: guideId,
    topicGuideId: topicGuideId || null,
    guideItem,
    isMobile,
    allowAutoplay,
    isTopicIdInvalid: !!hasTopicFetchFailed,
    isFreestarEnabled: selectExperiment(state, FREESTAR_ENABLED),
    isBottomBannerEnabled: selectExperiment(state, BOTTOM_BANNER_ENABLED),
    isFreestarPrimisVideoEnabled: selectExperiment(state, FREESTAR_PRIMIS_VIDEO_ENABLED),
  };
}

const actions = {
  fetchProfile,
  logClientError,
  logWebActivity,
};

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connectWithFailureState(getProfile),
  connect(mapStateToProps, mapDispatchToProps),
)(Profile);
